/**
 * The drag contract between the Captured drawer (the drag SOURCE) and the open
 * Segment (the drop TARGET).
 *
 * It is a named module, not two inline `dataTransfer` string literals in two
 * different components, for one reason: a source and a target that agree by
 * coincidence will silently stop agreeing, and a drag that silently does
 * nothing is EXACTLY the failure Kelly recorded (QA-52 f060–069: two attempts,
 * ~20 seconds, no ghost, no highlight, no error). One module, one MIME type,
 * one shape, asserted by tests on both sides.
 */

/**
 * A custom MIME type, not "text/plain". A capture dragged over an ordinary text
 * field must NOT paste a blob of JSON into it, and a random text selection
 * dragged onto a Segment must not look like a capture.
 */
export const CAPTURE_DRAG_MIME = "application/x-iyuga-capture";

/**
 * Written onto the dataTransfer at dragstart.
 *
 * QA-121: the payload carries every field `captureToBlock.js` needs to build
 * the Segment block (`media_url` for photo/video/audio/doc, `text_content` for
 * a note, `source_url` for a link) — not just `id`/`type`. `id`/`type` alone
 * round-trip fine but leave `isCaptureInsertable()` with no `media_url` to
 * check, so every media drop was rejected with "This capture is still
 * uploading, or its file is missing" regardless of the capture's real state —
 * the drop path never actually inspected the file, it inspected a payload
 * that was never given one.
 */
export const writeCaptureDrag = (dataTransfer, item) => {
  if (!dataTransfer || !item) return false;
  try {
    dataTransfer.setData(
      CAPTURE_DRAG_MIME,
      JSON.stringify({
        id: item.id,
        type: item.type,
        media_url: item.media_url ?? null,
        text_content: item.text_content ?? null,
        source_url: item.source_url ?? null,
      })
    );
    // `copy` (not `move`): the capture stays in the drawer and in the inbox —
    // adding it to a Segment does not consume it.
    // eslint-disable-next-line no-param-reassign
    dataTransfer.effectAllowed = "copy";
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Is the thing currently being dragged one of OUR captures?
 *
 * Read from `dataTransfer.types` rather than getData, because during dragover
 * browsers deliberately hide the payload — getData returns "" there. Checking
 * types is the only way a dropzone can decide whether to highlight BEFORE the
 * drop, which is what makes the affordance visible instead of a guess.
 */
export const isCaptureDrag = (dataTransfer) => {
  if (!dataTransfer || !dataTransfer.types) return false;
  return Array.from(dataTransfer.types).includes(CAPTURE_DRAG_MIME);
};

/** Read the dragged capture's identity at drop time. Null if it is not ours. */
export const readCaptureDrag = (dataTransfer) => {
  if (!isCaptureDrag(dataTransfer)) return null;
  try {
    const parsed = JSON.parse(dataTransfer.getData(CAPTURE_DRAG_MIME));
    if (!parsed || parsed.id === undefined || parsed.id === null) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};
